import { useParams } from 'react-router-dom';
import { PageContainer } from '../../components/PageContainer';
import { PermissionGuard } from '../../components/PermissionGuard';
import { NotFoundError, ServerError } from '../../components/ui/ErrorDisplay';
import { PageLoading } from '../../components/ui/LoadingSpinner';
import { useVenuePermissions } from '../../hooks/useVenuePermissions';
import { VenueHeader } from './VenueHeader';
import { VenueOverview } from './VenueOverview';
import { WelcomeBanner } from './WelcomeBanner';
import { WineManagementSection } from './WineManagementSection';
import { StatisticsSection } from './StatisticsSection';
import { EventsSection } from './EventsSection';
import { ExperiencesSection } from './ExperiencesSection';
import { ModerationOutcomesSection } from './ModerationOutcomesSection';

export const VenueManagement = () => {
  const { id } = useParams<{ id: string }>();

  // Venue data fetching and permission checking
  const { venue, isLoading, error } = useVenuePermissions({
    venueId: id || '',
  });

  // Loading state handling
  if (isLoading) {
    return (
      <div className="px-0 md:px-4 pt-2 pb-4 max-w-4xl mx-auto">
        <PageContainer>
          <PageLoading message="Loading venue management..." />
        </PageContainer>
      </div>
    );
  }

  // Error handling
  if (error) {
    return (
      <div className="px-0 md:px-4 pt-2 pb-4 max-w-4xl mx-auto">
        <PageContainer className="mt-2">
          <ServerError
            title="Error Loading Venue"
            message={`Unable to load venue management: ${error.message}`}
            onRetry={() => window.location.reload()}
          >
            <p className="text-sm text-gray-600 mt-2">
              Please try refreshing the page or contact support if the problem persists.
            </p>
          </ServerError>
        </PageContainer>
      </div>
    );
  }

  // Venue not found handling
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!venue) {
    return (
      <div className="px-0 md:px-4 pt-2 pb-4 max-w-4xl mx-auto">
        <PageContainer className="mt-2">
          <NotFoundError
            title="Venue Not Found"
            message="The venue you're looking for doesn't exist or may have been removed."
          />
        </PageContainer>
      </div>
    );
  }

  return (
    <PermissionGuard venueId={id || ''}>
      <div className="px-0 md:px-4 pt-2 pb-4 max-w-4xl mx-auto">
        <PageContainer className="mt-2">
          <VenueHeader venue={venue} />

          {/* Main content area */}
          <div className="pt-6">
            <WelcomeBanner venue={venue} />
            <VenueOverview venue={venue} />

            {/* Management sections with consistent visual hierarchy */}
            <div className="space-y-6 md:space-y-8">
              <WineManagementSection />
              <EventsSection />
              <ExperiencesSection />
              <ModerationOutcomesSection />
              <StatisticsSection />
            </div>
          </div>
        </PageContainer>
      </div>
    </PermissionGuard>
  );
};